import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Target, Plus, X, Pencil, Trash2, Clock, Milestone } from 'lucide-react';
import { CommentSection } from './CommentSection';

export function GoalsTab({
  goals, showGoalForm, setShowGoalForm, goalForm, setGoalForm,
  editingGoal, setEditingGoal,
  onCreateGoal, onUpdateGoal, onUpdateStatus, onEditGoal, onDeleteGoal,
  currentUserId, token,
}) {
  const resetGoalForm = () => {
    setShowGoalForm(false);
    setEditingGoal(null);
    setGoalForm({ title: '', description: '', target_date: '' });
  };

  return (
    <>
      {showGoalForm ? (
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              {editingGoal ? 'Edit Goal' : 'Create Goal'}
              <Button variant="ghost" size="sm" onClick={resetGoalForm}>
                <X className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editingGoal ? onUpdateGoal : onCreateGoal} className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Goal Title *</label>
                <Input value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} required placeholder="e.g., Complete MBA, Get Promoted to Manager" className="bg-slate-900 border-slate-600" data-testid="goal-title-input" />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Description</label>
                <textarea value={goalForm.description} onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })} rows={3} className="w-full bg-slate-900 border border-slate-600 rounded-md p-3 text-white" placeholder="Describe your goal and why it matters..." />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Target Date</label>
                <Input type="date" value={goalForm.target_date} onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })} className="bg-slate-900 border-slate-600" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-goal-btn">
                  <Target className="h-4 w-4 mr-2" />
                  {editingGoal ? 'Update Goal' : 'Create Goal'}
                </Button>
                <Button type="button" variant="outline" onClick={resetGoalForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Button onClick={() => setShowGoalForm(true)} className="bg-blue-600 hover:bg-blue-700 mb-6" data-testid="add-goal-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Goal
        </Button>
      )}

      <div className="space-y-4">
        {goals.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-8 text-center">
              <Target className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No goals yet. Share your aspirations with your family!</p>
            </CardContent>
          </Card>
        ) : goals.map(goal => (
          <Card key={goal.goal_id} className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-white">{goal.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    {goal.user_name}
                    {goal.target_date && (
                      <span className="flex items-center gap-1 inline-flex ml-2">
                        <Clock className="h-3 w-3" /> Target: {new Date(goal.target_date).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                </div>
                {goal.user_id === currentUserId ? (
                  <Select value={goal.status || 'not_started'} onValueChange={(value) => onUpdateStatus(goal.goal_id, value)}>
                    <SelectTrigger className={`w-[140px] h-8 text-xs font-medium border-0 ${
                      goal.status === 'completed' ? 'bg-green-600 text-white' :
                      goal.status === 'in_progress' ? 'bg-blue-600 text-white' :
                      goal.status === 'on_hold' ? 'bg-yellow-600 text-black' : 'bg-slate-600 text-white'
                    }`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="not_started" className="text-slate-300 focus:bg-slate-700 focus:text-white">Not Started</SelectItem>
                      <SelectItem value="in_progress" className="text-blue-400 focus:bg-slate-700 focus:text-blue-300">In Progress</SelectItem>
                      <SelectItem value="on_hold" className="text-yellow-400 focus:bg-slate-700 focus:text-yellow-300">On Hold</SelectItem>
                      <SelectItem value="completed" className="text-green-400 focus:bg-slate-700 focus:text-green-300">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={
                    goal.status === 'completed' ? 'bg-green-600' :
                    goal.status === 'in_progress' ? 'bg-blue-600' :
                    goal.status === 'on_hold' ? 'bg-yellow-600' : 'bg-slate-600'
                  }>
                    {goal.status?.replace('_', ' ') || 'Not Started'}
                  </Badge>
                )}
              </div>
              {goal.description && <p className="text-slate-300 mb-4">{goal.description}</p>}
              {goal.mentor_name && <p className="text-amber-400 text-sm">Mentor: {goal.mentor_name}</p>}
              {goal.milestones?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400 mb-2">Milestones:</p>
                  <div className="space-y-2">
                    {goal.milestones.map(m => (
                      <div key={m.milestone_id} className="flex items-center gap-2 text-sm text-slate-300">
                        <Milestone className="h-3 w-3 text-green-400" />
                        {m.title}
                        {m.achieved_date && <span className="text-slate-500">({new Date(m.achieved_date).toLocaleDateString()})</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-700">
                <Button variant="ghost" size="sm" onClick={() => onEditGoal(goal)} className="text-slate-400 hover:text-blue-400">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onDeleteGoal(goal.goal_id)} className="text-slate-400 hover:text-red-400">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </div>
              <CommentSection itemType="goal" itemId={goal.goal_id} token={token} />
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
